import type {
  AdminFlowCanvasDrag,
  AdminFlowCanvasPan,
  AdminFlowCanvasZoomAnchor,
  AdminFlowWhiteboardProps,
} from "../models/admin-flow-whiteboard.models";
import type { AdminFlowNode, AdminFlowTool } from "../models/admin-flows.models";
import {
  ADMIN_FLOW_CARD_HEIGHT,
  ADMIN_FLOW_CARD_WIDTH,
  ADMIN_FLOW_SOURCE_OPTIONS,
} from "../utils/admin-flow-whiteboard.constants";
import { getAdminFlowSourceArguments } from "../utils/admin-flow-whiteboard.utils";
import { connectAdminFlowNodes, createAdminFlowNode } from "../utils/admin-flows.utils";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent, MouseEvent } from "react";

// Keep zoom, selection and pointer sessions local to Whiteboard.
// Every content edit goes back through the page-owned draft so Cards sees the same graph.
export const useAdminFlowCanvas = ({ flow, onChange }: Pick<AdminFlowWhiteboardProps, "flow" | "onChange">) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<AdminFlowCanvasDrag>();
  const panRef = useRef<AdminFlowCanvasPan>();
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const zoomAnchor = useRef<AdminFlowCanvasZoomAnchor>();
  const spaceRef = useRef(false);
  const suppressPanClick = useRef(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const [panning, setPanning] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [connectingFrom, setConnectingFrom] = useState<string>();
  const [error, setError] = useState("");
  const selectedNode = flow.nodes.find((node) => node.id === selectedId);

  const updateNode = (node: AdminFlowNode) => {
    onChange({ ...flow, nodes: flow.nodes.map((current) => (current.id === node.id ? node : current)) });
  };

  const addNode = (kind: AdminFlowNode["kind"], source?: string, tool?: AdminFlowTool) => {
    const node = createAdminFlowNode(kind, flow.nodes.length);
    const sourceLabel = ADMIN_FLOW_SOURCE_OPTIONS.find((option) => option.value === source)?.label;
    const nextNode = source
      ? { ...node, source, label: sourceLabel || node.label, arguments: getAdminFlowSourceArguments(source) }
      : tool
        ? { ...node, toolName: tool.name, toolNames: [tool.name], label: tool.name.slice(0, 120) }
        : node;
    onChange({ ...flow, nodes: [...flow.nodes, nextNode] });
    setSelectedId(nextNode.id);
    setError("");
  };

  const deleteNode = () => {
    if (!selectedNode) {
      return;
    }

    onChange({
      ...flow,
      nodes: flow.nodes.filter((node) => node.id !== selectedNode.id),
      edges: flow.edges.filter((edge) => edge.from !== selectedNode.id && edge.to !== selectedNode.id),
    });
    setSelectedId(undefined);
    setConnectingFrom(undefined);
  };

  const finishConnection = (to: string) => {
    if (!connectingFrom) {
      setError("Choose an output port first, then choose this input port.");

      return;
    }

    try {
      onChange(connectAdminFlowNodes(flow, connectingFrom, to));
      setError("");
    } catch (connectionError) {
      setError(connectionError instanceof Error ? connectionError.message : "These blocks cannot be connected.");
    }

    setConnectingFrom(undefined);
  };

  const beginNodeDrag = (event: PointerEvent<HTMLButtonElement>, node: AdminFlowNode) => {
    if (event.button !== 0) {
      return;
    }

    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectedId(node.id);
    dragRef.current = {
      pointerId: event.pointerId,
      nodeId: node.id,
      startX: event.clientX,
      startY: event.clientY,
      nodeX: node.x,
      nodeY: node.y,
    };
  };

  const moveNode = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;

    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    const node = flow.nodes.find((entry) => entry.id === drag.nodeId);

    if (node) {
      updateNode({
        ...node,
        x: Math.max(24, Math.min(4000, drag.nodeX + (event.clientX - drag.startX) / zoom)),
        y: Math.max(24, Math.min(4000, drag.nodeY + (event.clientY - drag.startY) / zoom)),
      });
    }
  };

  const finishNodeDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragRef.current = undefined;
  };

  const moveNodeWithKeyboard = (event: KeyboardEvent<HTMLButtonElement>, node: AdminFlowNode) => {
    const distance = event.shiftKey ? 64 : 16;
    const horizontal = event.key === "ArrowLeft" ? -distance : event.key === "ArrowRight" ? distance : 0;
    const vertical = event.key === "ArrowUp" ? -distance : event.key === "ArrowDown" ? distance : 0;

    if (horizontal || vertical) {
      event.preventDefault();
      updateNode({
        ...node,
        x: Math.max(24, Math.min(4000, node.x + horizontal)),
        y: Math.max(24, Math.min(4000, node.y + vertical)),
      });
    }
  };

  const beginPan = (event: PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;
    suppressPanClick.current = false;
    const isBackground =
      event.target === viewport ||
      (event.target instanceof HTMLElement && event.target.dataset.test === "admin-flow-canvas");

    if (!viewport || (event.button !== 1 && (event.button !== 0 || (!spaceRef.current && !isBackground)))) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    viewport.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    suppressPanClick.current = true;
    setPanning(true);
    panRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
    };
  };

  const movePan = (event: PointerEvent<HTMLDivElement>) => {
    const pan = panRef.current;
    const viewport = viewportRef.current;

    if (pan && viewport && pan.pointerId === event.pointerId) {
      viewport.scrollLeft = pan.scrollLeft - (event.clientX - pan.startX);
      viewport.scrollTop = pan.scrollTop - (event.clientY - pan.startY);
    }
  };

  const finishPan = (event: PointerEvent<HTMLDivElement>) => {
    if (panRef.current?.pointerId !== event.pointerId) return;

    panRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setPanning(false);
  };

  const ignorePanClick = (event: MouseEvent) => {
    if (!suppressPanClick.current) return false;

    suppressPanClick.current = false;
    event.preventDefault();
    event.stopPropagation();

    return true;
  };

  const changeZoom = useCallback((value: number, x?: number, y?: number) => {
    const next = Math.max(0.35, Math.min(1.75, value));
    const viewport = viewportRef.current;

    if (next === zoomRef.current) return;

    if (viewport) {
      const left = x ?? viewport.clientWidth / 2;
      const top = y ?? viewport.clientHeight / 2;
      zoomAnchor.current = {
        x: (viewport.scrollLeft + left) / zoomRef.current,
        y: (viewport.scrollTop + top) / zoomRef.current,
        left,
        top,
      };
    }

    zoomRef.current = next;
    setZoom(next);
  }, []);

  // Apply the scroll anchor after React has resized the scaled canvas.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const anchor = zoomAnchor.current;

    if (viewport && anchor) {
      viewport.scrollLeft = anchor.x * zoom - anchor.left;
      viewport.scrollTop = anchor.y * zoom - anchor.top;
    }

    zoomAnchor.current = undefined;
  }, [zoom]);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) return;

    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientHeight : 1);
      changeZoom(
        zoomRef.current * Math.exp(-Math.max(-200, Math.min(200, delta)) * 0.002),
        event.clientX - rect.left,
        event.clientY - rect.top,
      );
    };
    viewport.addEventListener("wheel", wheel, { passive: false });

    return () => viewport.removeEventListener("wheel", wheel);
  }, [changeZoom]);

  useEffect(() => {
    const keyDown = (event: globalThis.KeyboardEvent) => {
      const target = event.target;

      if (event.code !== "Space" || event.altKey || event.ctrlKey || event.metaKey || !(target instanceof HTMLElement))
        return;
      if (
        !target.closest('[data-test="admin-flow-whiteboard"]') ||
        target.closest('input, textarea, select, [contenteditable="true"]')
      )
        return;
      if (target.closest("button, summary") && !viewportRef.current?.contains(target)) return;

      event.preventDefault();
      spaceRef.current = true;
      setSpacePressed(true);
    };
    const releaseSpace = () => {
      spaceRef.current = false;
      setSpacePressed(false);
    };
    const keyUp = (event: globalThis.KeyboardEvent) => {
      if (event.code === "Space") releaseSpace();
    };
    const blur = () => {
      releaseSpace();
      const pan = panRef.current;
      panRef.current = undefined;

      if (pan && viewportRef.current?.hasPointerCapture(pan.pointerId))
        viewportRef.current.releasePointerCapture(pan.pointerId);
      setPanning(false);
    };
    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);
    window.addEventListener("blur", blur);

    return () => {
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
      window.removeEventListener("blur", blur);
    };
  }, []);

  const fitCanvas = () => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const width = Math.max(400, ...flow.nodes.map((node) => node.x + ADMIN_FLOW_CARD_WIDTH + 48));
    const height = Math.max(260, ...flow.nodes.map((node) => node.y + ADMIN_FLOW_CARD_HEIGHT + 48));
    changeZoom(Math.min(viewport.clientWidth / width, viewport.clientHeight / height, 1));
    zoomAnchor.current = undefined;
    viewport.scrollLeft = 0;
    viewport.scrollTop = 0;
  };

  return {
    viewportRef,
    zoom,
    spacePressed,
    panning,
    ignorePanClick,
    changeZoom,
    fitCanvas,
    selectedNode,
    setSelectedId,
    connectingFrom,
    setConnectingFrom,
    error,
    setError,
    updateNode,
    addNode,
    deleteNode,
    finishConnection,
    beginNodeDrag,
    moveNode,
    finishNodeDrag,
    moveNodeWithKeyboard,
    beginPan,
    movePan,
    finishPan,
  };
};
